import React, { useState, useContext, useEffect, useMemo, useRef } from "react";
import styles from "./styles.module.css";
import { Typography, Toolbar, Collapse } from "@mui/material";
import { Context } from "../../config/Context/AuthContext";
import LogoutIcon from "@mui/icons-material/Logout";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import MuiAppBar from "@mui/material/AppBar";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import logo from "../../images/logoBrancoPrincipal.png";
import Link from "@mui/material/Link";

import { List, ListItem, ListItemButton, ListItemText } from "@mui/material";
import { Outlet, useNavigate } from "react-router-dom";
import {
  ExpandLess,
  ExpandMore,
  NotificationsActive,
} from "@mui/icons-material";
import { LoginContext } from "../../config/Context/LoginContext";
import { usePermissoes } from "../../config/Context/PermissoesContext";
import { filtrarMenuPorPermissoes } from "../../utils/permissoesMenu.util";

import BadgeNotification from "@mui/material/Badge";

import NotificationsIcon from "@mui/icons-material/Notifications";
import { getSocket } from "../../utils/webSocket.util";
import { NomeCompleto } from "../../utils/localStorage.util";
import Notificacao from "../Notificacao";
import AlertNotification from "./AlertNotification";

const drawerWidth = 240;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const normaliza = (texto) =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const HREF_APURACOES = "/relatorios/apuracoes";
const HREF_PROCESSAMENTO_RELATORIOS = "/relatorios/processamento";

const HREF_PARCEIROS_ASSOCIADOS = "/acessos/parceiros-associados";
const HREF_CONTAS_RESULTADO = "/parametrizacoes/contas-resultado";
const HREF_ATUALIZAR_SELIC = "/parametrizacoes/atualizar-selic";
const HREF_IMPOSTOS_ESOCIAL = "/parametrizacoes/impostos-esocial";
const HREF_TRIBUTARIAS = "/parametrizacoes/tributarias";
const HREF_TABELA_NCM = "/parametrizacoes/tabela-ncm";

/** Inclui o link Parceiros e Associados no submenu de Cadastros (menu vindo do backend). */
function anexarParceirosAoMenuCadastros(items) {
  if (!Array.isArray(items)) return items;
  return items.map((item) => {
    const menuNorm = normaliza(item.menu || "");
    if (!menuNorm.includes("cadastro")) return item;
    const submenu = Array.isArray(item.submenu) ? [...item.submenu] : [];
    const jaExiste = submenu.some(
      (s) => (s.href || "").toLowerCase() === HREF_PARCEIROS_ASSOCIADOS.toLowerCase()
    );
    if (jaExiste) return item;
    return {
      ...item,
      submenu: [
        ...submenu,
        {
          idSubMenu: 9995,
          submenu: "Parceiros e Associados",
          descricao: "Parceiros e Associados",
          href: HREF_PARCEIROS_ASSOCIADOS,
        },
      ],
    };
  });
}

/** Inclui Impostos INSS (eSocial) no submenu Parametrização. */
function anexarImpostosEsocialAoMenuParametrizacao(items) {
  if (!Array.isArray(items)) return items;
  return items.map((item) => {
    const menuNorm = normaliza(item.menu || "");
    if (!menuNorm.includes("parametriz")) return item;
    const submenu = Array.isArray(item.submenu) ? [...item.submenu] : [];
    const jaExiste = submenu.some(
      (s) => (s.href || "").toLowerCase() === HREF_IMPOSTOS_ESOCIAL.toLowerCase()
    );
    if (jaExiste) return item;
    return {
      ...item,
      submenu: [
        ...submenu,
        {
          idSubMenu: 9994,
          submenu: "Impostos INSS",
          descricao: "Impostos INSS (eSocial)",
          href: HREF_IMPOSTOS_ESOCIAL,
        },
      ],
    };
  });
}

/** Inclui Tributárias (params 20S) no submenu Parametrização. */
function anexarTributariasAoMenuParametrizacao(items) {
  if (!Array.isArray(items)) return items;
  return items.map((item) => {
    const menuNorm = normaliza(item.menu || "");
    if (!menuNorm.includes("parametriz")) return item;
    const submenu = Array.isArray(item.submenu) ? [...item.submenu] : [];
    const jaExiste = submenu.some(
      (s) => (s.href || "").toLowerCase() === HREF_TRIBUTARIAS.toLowerCase()
    );
    if (jaExiste) return item;
    return {
      ...item,
      submenu: [
        ...submenu,
        {
          idSubMenu: 9993,
          submenu: "Tributárias",
          descricao: "Tributárias (Cálculo 20S)",
          href: HREF_TRIBUTARIAS,
        },
      ],
    };
  });
}

/** Inclui Contas de Resultado no submenu Parametrização. */
function anexarContasResultadoAoMenuParametrizacao(items) {
  if (!Array.isArray(items)) return items;
  return items.map((item) => {
    const menuNorm = normaliza(item.menu || "");
    if (!menuNorm.includes("parametriz")) return item;
    const submenu = Array.isArray(item.submenu) ? [...item.submenu] : [];
    const jaExiste = submenu.some(
      (s) => (s.href || "").toLowerCase() === HREF_CONTAS_RESULTADO.toLowerCase()
    );
    if (jaExiste) return item;
    return {
      ...item,
      submenu: [
        ...submenu,
        {
          idSubMenu: 9998,
          submenu: "Contas de Resultado",
          descricao: "Contas de Resultado",
          href: HREF_CONTAS_RESULTADO,
        },
      ],
    };
  });
}

/** Inclui o link Tabela Selic no submenu de Parametrização (menu vindo do backend). */
function anexarAtualizarSelicAoMenuParametrizacao(items) {
  if (!Array.isArray(items)) return items;
  return items.map((item) => {
    const menuNorm = normaliza(item.menu || "");
    if (!menuNorm.includes("parametriz")) return item;
    const submenu = Array.isArray(item.submenu) ? [...item.submenu] : [];
    const jaExiste = submenu.some(
      (s) => (s.href || "").toLowerCase() === HREF_ATUALIZAR_SELIC.toLowerCase()
    );
    if (jaExiste) return item;
    return {
      ...item,
      submenu: [
        ...submenu,
        {
          idSubMenu: 9996,
          submenu: "Tabela Selic",
          descricao: "Tabela Selic",
          href: HREF_ATUALIZAR_SELIC,
        },
      ],
    };
  });
}

/** Inclui o link Tabela NCM no submenu de Parametrização (menu vindo do backend). */
function anexarTabelaNcmAoMenuParametrizacao(items) {
  if (!Array.isArray(items)) return items;
  return items.map((item) => {
    const menuNorm = normaliza(item.menu || "");
    if (!menuNorm.includes("parametriz")) return item;
    const submenu = Array.isArray(item.submenu) ? [...item.submenu] : [];
    const jaExiste = submenu.some(
      (s) => (s.href || "").toLowerCase() === HREF_TABELA_NCM.toLowerCase()
    );
    if (jaExiste) return item;
    return {
      ...item,
      submenu: [
        ...submenu,
        {
          idSubMenu: 9992,
          submenu: "Tabela NCM",
          descricao: "Tabela NCM",
          href: HREF_TABELA_NCM,
        },
      ],
    };
  });
}

/** Garante Apurações e Processamento no submenu Relatórios (Processamento por último). */
function anexarProcessamentoAoFinalDoSubmenuRelatorios(items) {
  if (!Array.isArray(items)) return items;
  return items.map((item) => {
    if (!item?.submenu?.length) return item;
    const menuLower = (item.menu || "").toLowerCase();
    if (!menuLower.includes("relat")) return item;
    const hrefsFixos = new Set([
      HREF_APURACOES.toLowerCase(),
      HREF_PROCESSAMENTO_RELATORIOS.toLowerCase(),
    ]);
    const semDuplicata = item.submenu.filter(
      (s) => !hrefsFixos.has((s.href || "").toLowerCase())
    );
    return {
      ...item,
      submenu: [
        ...semDuplicata,
        {
          idSubMenu: 9996,
          submenu: "Apurações",
          descricao: "Apurações",
          href: HREF_APURACOES,
        },
        {
          idSubMenu: 9997,
          submenu: "Processamento",
          descricao: "Processamento",
          href: HREF_PROCESSAMENTO_RELATORIOS,
        },
      ],
    };
  });
}

function comporMenu(items) {
  return anexarParceirosAoMenuCadastros(
    anexarProcessamentoAoFinalDoSubmenuRelatorios(
      removerSubmenuIcmsStDoMenuRelatorios(
        anexarTabelaNcmAoMenuParametrizacao(
          anexarAtualizarSelicAoMenuParametrizacao(
            anexarTributariasAoMenuParametrizacao(
              anexarImpostosEsocialAoMenuParametrizacao(
                anexarContasResultadoAoMenuParametrizacao(items)
              )
            )
          )
        )
      )
    )
  );
}

/** Remove atalho duplicado ICMS-ST do submenu Relatórios (itensMenu / API). */
function removerSubmenuIcmsStDoMenuRelatorios(items) {
  if (!Array.isArray(items)) return items;
  return items.map((item) => {
    if (!item?.submenu?.length) return item;
    const menuLower = (item.menu || "").toLowerCase();
    if (!menuLower.includes("relat")) return item;
    return {
      ...item,
      submenu: item.submenu.filter((sub) => {
        const label = `${sub.submenu || ""} ${sub.descricao || ""}`
          .toLowerCase()
          .replace(/\s+/g, " ");
        const href = (sub.href || "").toLowerCase();
        if (href.includes("icms-st") || href.includes("icmsst")) return false;
        if (/icms[\s\-_]?st/i.test(label)) return false;
        return true;
      }),
    };
  });
}

function normalizarHrefCadastros(href) {
  if (typeof href !== "string") return href;
  if (href.startsWith("/acessos/usuarios")) {
    return href.replace(/^\/acessos\/usuarios/, "/cadastros/usuarios");
  }
  if (href.startsWith("/cadastros/empresas/grupos-economicos")) {
    return href.replace(
      /^\/cadastros\/empresas\/grupos-economicos/,
      "/cadastros/grupos-economicos"
    );
  }
  if (href.startsWith("/empresas/grupos-economicos")) {
    return href.replace(
      /^\/empresas\/grupos-economicos/,
      "/cadastros/grupos-economicos"
    );
  }
  if (href.startsWith("/empresas")) {
    return href.replace(/^\/empresas/, "/cadastros/empresas");
  }
  return href;
}

function normalizarHrefsItensMenu(items) {
  if (!Array.isArray(items)) return items;
  return items.map((item) => {
    if (!item) return item;
    const next = { ...item };
    if (Array.isArray(next.submenu)) {
      next.submenu = next.submenu.map((sub) => {
        if (!sub || typeof sub.href !== "string") return sub;
        const href = normalizarHrefCadastros(sub.href);
        return href === sub.href ? sub : { ...sub, href };
      });
    }
    return next;
  });
}

export default function Header() {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [menuBase, setMenuBase] = useState([]);
  const { pode, carregado } = usePermissoes();
  const menuItems = useMemo(
    () => filtrarMenuPorPermissoes(menuBase, pode, Boolean(carregado)),
    [menuBase, pode, carregado]
  );
  const navigate = useNavigate();
  const { dadosLogin } = useContext(LoginContext);
  const { authenticated, handleLogout } = useContext(Context);
  const [openNotifications, setOpenNotifications] = useState(false);
  const [openAlertNotification, setOpenAlertNotification] = useState(false);
  const [dataNotifications, setDataNotifications] = useState();
  const [textNotifications, setTextNotifications] = useState();

  const [qtdNotifications, setQtdNotifications] = useState(0);

  const [primVez, setPrimVez] = useState(0);

  const [openMenus, setOpenMenus] = useState([]);

  const authRend = authenticated !== undefined ? authenticated : true;

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    const storedMenuItems = localStorage.getItem("itensMenu");
    if (storedMenuItems) {
      try {
        const parsedItems = JSON.parse(storedMenuItems);

        // Validar se parsedItems é um array
        if (!Array.isArray(parsedItems)) {
          setMenuBase([]);
          return;
        }

        const itemsComHrefs = normalizarHrefsItensMenu(parsedItems);

        // Mapear nomes de menus conforme solicitado
        const itemsMapeados = itemsComHrefs
          .filter(item => {
            // Remover menu "Menu" ou "Dashboard" da lista
            if (!item || !item.menu) return false;
            const menuNome = item.menu.toLowerCase();
            return menuNome !== 'menu' && menuNome !== 'dashboard';
          })
          .map(item => {
            if (!item || !item.menu) return item;
            
            let menuAtualizado = { ...item };
            
            // Acessos -> Cadastros
            if (item.menu === "Acessos") {
              menuAtualizado.menu = "Cadastros";
              
              // Filtrar submenus: remover "Funcionalidades" e renomear "Grupos de Usuários"
              if (menuAtualizado.submenu && Array.isArray(menuAtualizado.submenu)) {
                menuAtualizado.submenu = menuAtualizado.submenu
                  .filter(sub => {
                    const submenuText = (sub.submenu || sub.descricao || '').toLowerCase();
                    return !submenuText.includes('funcionalidade');
                  })
                  .map(sub => {
                    if ((sub.submenu || '').includes('Grupos de Usuários')) {
                      return {
                        ...sub,
                        submenu: 'Grupos',
                        descricao: 'Grupos',
                      };
                    }
                    return sub;
                  });
              }
            }
            
            return menuAtualizado;
          });

        // Encontrar menu Empresas e Grupos Econômicos para mover para Cadastros
        const menuEmpresas = itemsMapeados.find(item => 
          item && item.menu && item.menu.toLowerCase().includes('empresa')
        );
        
        const menuCadastros = itemsMapeados.find(item => 
          item && item.menu === 'Cadastros'
        );

        // Mover submenus de Empresas para Cadastros (ou criar Cadastros se não existir)
        if (menuEmpresas) {
          const subsEmpresas = Array.isArray(menuEmpresas.submenu) ? menuEmpresas.submenu : [];
          if (menuCadastros) {
            // Mesclar em Cadastros existente
            if (!menuCadastros.submenu) menuCadastros.submenu = [];
            menuCadastros.submenu = [...menuCadastros.submenu, ...subsEmpresas];
          } else if (subsEmpresas.length > 0) {
            // Criar menu Cadastros a partir do menu Empresas
            const novoMenuCadastros = { ...menuEmpresas, menu: 'Cadastros', submenu: subsEmpresas };
            itemsMapeados.push(novoMenuCadastros);
          }
          // Remover menu Empresas da lista
          const itemsFinais = itemsMapeados.filter(item => 
            !item || !item.menu || !item.menu.toLowerCase().includes('empresa')
          );
          setMenuBase(comporMenu(itemsFinais));
          return;
        }

        // Validar estrutura de cada item antes de definir. Importações vem apenas do catálogo/permissões.
        const itemsValidos = itemsMapeados.filter(item => {
          if (!item || !item.menu) {
            return false;
          }
          return true;
        });

        const itemsComProcessamento = comporMenu(itemsValidos);

        setMenuBase(itemsComProcessamento);
        try {
          localStorage.setItem("itensMenu", JSON.stringify(itemsComProcessamento));
        } catch {
          /* ignore */
        }
      } catch (error) {
        setMenuBase([]);
      }
    } else {
      setMenuBase([]);
    }
  }, []);

  // Recarregar menus quando permissões mudarem
  useEffect(() => {
    if (!carregado) return;

    const storedMenuItems = localStorage.getItem("itensMenu");
    if (storedMenuItems) {
      try {
        const parsedItems = JSON.parse(storedMenuItems);
        if (!Array.isArray(parsedItems)) {
          setMenuBase([]);
          return;
        }

        const itemsComHrefs = normalizarHrefsItensMenu(parsedItems);
        const itemsMapeados = itemsComHrefs
          .filter(item => {
            if (!item || !item.menu) return false;
            const menuNome = item.menu.toLowerCase();
            return menuNome !== 'menu' && menuNome !== 'dashboard';
          })
          .map(item => {
            if (!item || !item.menu) return item;
            let menuAtualizado = { ...item };
            if (item.menu === "Acessos") {
              menuAtualizado.menu = "Cadastros";
              if (menuAtualizado.submenu && Array.isArray(menuAtualizado.submenu)) {
                menuAtualizado.submenu = menuAtualizado.submenu
                  .filter(sub => {
                    const submenuText = (sub.submenu || sub.descricao || '').toLowerCase();
                    return !submenuText.includes('funcionalidade');
                  })
                  .map(sub => {
                    if ((sub.submenu || '').includes('Grupos de Usuários')) {
                      return { ...sub, submenu: 'Grupos', descricao: 'Grupos' };
                    }
                    return sub;
                  });
              }
            }
            return menuAtualizado;
          });

        const menuEmpresas = itemsMapeados.find(item =>
          item && item.menu && item.menu.toLowerCase().includes('empresa')
        );
        const menuCadastros = itemsMapeados.find(item => item && item.menu === 'Cadastros');

        if (menuEmpresas) {
          const subsEmpresas = Array.isArray(menuEmpresas.submenu) ? menuEmpresas.submenu : [];
          if (menuCadastros) {
            if (!menuCadastros.submenu) menuCadastros.submenu = [];
            menuCadastros.submenu = [...menuCadastros.submenu, ...subsEmpresas];
          } else if (subsEmpresas.length > 0) {
            itemsMapeados.push({ ...menuEmpresas, menu: 'Cadastros', submenu: subsEmpresas });
          }
          const itemsFinais = itemsMapeados.filter(item =>
            !item || !item.menu || !item.menu.toLowerCase().includes('empresa')
          );
          setMenuBase(comporMenu(itemsFinais));
          return;
        }

        const itemsValidos = itemsMapeados.filter(item => item && item.menu);
        setMenuBase(comporMenu(itemsValidos));
      } catch (error) {
        console.error('[Header] Erro ao recarregar menus:', error);
        setMenuBase([]);
      }
    } else {
      setMenuBase([]);
    }
  }, [carregado]);
  useEffect(() => {
    setPrimVez(primVez + 1);
  }, [qtdNotifications]);

  useEffect(() => {
    if (primVez >= 3 && qtdNotifications !== 0) {
      setTextNotifications({
        text: dataNotifications[0].texto,
        path: dataNotifications[0].path,
      });
      setOpenAlertNotification(true);

      setTimeout(() => {
        setOpenAlertNotification(false);
      }, 5000);
    }
  }, [primVez]);
  const logout = async () => {
    await handleLogout("/dashboard");
    navigate("/");
  };

  const toDashboard = () => {
    navigate("/dashboard");
  };

  const formatDate = (dthora) => {
    const date = new Date(dthora);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} - ${hours}:${minutes}`;
  };

  const handleOpenNotifications = () => {
    setOpenNotifications(true);
  };

  const handleCloseNotifications = () => {
    setOpenNotifications(false);
    // getSocket().emit("atualizar-notificacao-lida");
  };

  const handleNotifications = () => {
    openNotifications ? handleCloseNotifications() : handleOpenNotifications();
  };

  const handleMenuClick = (menuId) => {
    if (openMenus.includes(menuId)) {
      setOpenMenus(openMenus.filter((id) => id !== menuId));
    } else {
      setOpenMenus([menuId]);
    }
  };

  const handleSubmenuClick = (submenu) => {
    if (submenu.href) {
      navigate(submenu.href);
      setOpen(false);
      setOpenMenus([]);
    }
  };

  // p.o pediu pra chumbar a ordem do submenu
  const ordemManualSubmenu = [
    "PERDCOMP",
    "Extrato",
    "COMP-HOMO",
    "Compensações",
    "Projetos",
    "INSS",
    "PIS/COFINS",
    "IRPJ-CSLL",
    "ICMS",
    "ISS",
    "Ação Judicial",
    "C.E",
  ];

  /** Ordem fixa em Cadastros: Usuários → Grupos (permissões) → Empresas→ Grupos Econômicos */
  const rankHrefCadastros = (href) => {
    const h = (href || "").toLowerCase();
    if (h.includes("/cadastros/usuarios")) return 1;
    if (h.includes("/acessos/grupos-usuarios") || h.includes("/cadastros/grupos-usuarios"))
      return 2;
    if (
      h.includes("/cadastros/grupos-economicos") ||
      h.includes("/cadastros/empresas/grupos-economicos")
    )
      return 4;
    if (h.includes("/cadastros/empresas")) return 3;
    return 50;
  };

  const textoExibicaoCadastros = (submenu) => {
    const h = (submenu.href || "").toLowerCase();
    if (h.includes("/cadastros/usuarios")) return "Usuários";
    if (h.includes("/acessos/grupos-usuarios") || h.includes("/cadastros/grupos-usuarios"))
      return "Grupos (permissões)";
    if (
      h.includes("/cadastros/grupos-economicos") ||
      h.includes("/cadastros/empresas/grupos-economicos")
    )
      return "Grupos Econômicos";
    if (h.includes("/cadastros/empresas")) return "Empresas";
    return submenu.submenu ?? submenu.descricao ?? "Sem nome";
  };

  const renderSubmenus = (submenus, menuId, menu) => {
    // Validar se submenus existe e é um array
    if (!submenus || !Array.isArray(submenus) || submenus.length === 0) {
      return null;
    }

    let submenusOrdenados = submenus;

    if (menu === "Conta Corrente") {
      submenusOrdenados = [...submenus].sort((a, b) => {
        const nomeA = normaliza(a.submenu ?? a.descricao ?? "");
        const nomeB = normaliza(b.submenu ?? b.descricao ?? "");

        const indexA = ordemManualSubmenu.findIndex(
          (item) => normaliza(item) === nomeA
        );
        const indexB = ordemManualSubmenu.findIndex(
          (item) => normaliza(item) === nomeB
        );

        return indexA - indexB;
      });
    }

    if (menu === "Cadastros") {
      submenusOrdenados = [...submenus].sort(
        (a, b) => rankHrefCadastros(a.href) - rankHrefCadastros(b.href)
      );
    }

    return (
      <List>
        {submenusOrdenados.map((submenu) => {
          const submenuText =
            menu === "Cadastros"
              ? textoExibicaoCadastros(submenu)
              : submenu.submenu ?? submenu.descricao ?? "Sem nome";
          const submenuHref = submenu.href;

          return (
            <ListItem key={submenu.idSubMenu ?? submenu.id} disablePadding>
              <ListItemButton onClick={() => handleSubmenuClick(submenu)}>
                <ListItemText primary={submenuText} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    );
  };

  return (
    <>
      <div>
        <Box sx={{ display: "flex" }}>
          <CssBaseline />
          <AppBar
            position="fixed"
            open={open}
            sx={{ backgroundColor: "rgba(15, 32, 71, 0.3)" }}
          >
            <Toolbar
              sx={{
                display: "grid",
                gridGap: 20,
                ...(open && { gridTemplateColumns: "repeat(2, 1fr)" }),
                ...(!open && { gridTemplateColumns: "repeat(3, 1fr)" }),
              }}
            >
              {authRend ? (
                (dadosLogin.status === "APROVADO" || dadosLogin.status === "ok") ? (
                  <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    onClick={handleDrawerOpen}
                    sx={{
                      mr: 2,
                      justifySelf: "flex-start",
                      ...(open && { display: "none" }),
                    }}
                  >
                    <MenuIcon />
                  </IconButton>
                ) : (
                  <div></div>
                )
              ) : (
                <div></div>
              )}
              <Box
                sx={{
                  display: "flex",
                  ...(!open && { justifyContent: "center" }),
                }}
              >
                <Link component="button" onClick={toDashboard}>
                  <img src={logo} className={styles.logoMenu} alt="Logo"></img>
                </Link>
              </Box>
              <Box
                sx={{
                  display: "inline-flex",
                  gap: "3rem",
                  justifySelf: "flex-end",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {authRend && (
                  <BadgeNotification
                    badgeContent={qtdNotifications}
                    color="error"
                  >
                    <div className={styles.notification__Btn}>
                      {openNotifications ? (
                        <NotificationsActive
                          onClick={handleNotifications}
                          style={{ color: "#fff", fontSize: "1.5rem" }}
                        />
                      ) : (
                        <NotificationsIcon
                          onClick={handleNotifications}
                          style={{ color: "#fff", fontSize: "1.5rem" }}
                        />
                      )}
                    </div>
                  </BadgeNotification>
                )}

                {Array.isArray(dataNotifications) &&
                  dataNotifications.length > 0 ? (
                  <Notificacao
                    HandleOpen={openNotifications}
                    dataNotifications={dataNotifications}
                    handleCloseNotifications={handleCloseNotifications}
                  />
                ) : (
                  ""
                )}
                {authRend && (
                  <Box className={styles.userLogoutWrapper}>
                    <Typography
                      className={styles.userNameHeader}
                      component="span"
                      variant="body2"
                      title={NomeCompleto.getNomeCompleto() || undefined}
                    >
                      {NomeCompleto.getNomeCompleto() || "Usuário"}
                    </Typography>
                    <IconButton color="inherit" onClick={logout} aria-label="Sair">
                      <LogoutIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Toolbar>
          </AppBar>
          <Drawer
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              "& .MuiDrawer-paper": {
                width: drawerWidth,
                boxSizing: "border-box",
              },
            }}
            variant="persistent"
            anchor="left"
            open={open}
          >
            <DrawerHeader
              className={styles.cabecalhoMenuSecoes}
              sx={{
                paddingLeft: "16px",
                justifyContent: "space-between",
              }}
            >
              <Typography sx={{ fontWeight: "bold" }}>Dashboard</Typography>
              <IconButton onClick={handleDrawerClose}>
                {theme.direction === "ltr" ? (
                  <ChevronLeftIcon
                    className={styles.iconeCabecalhoMenuSecoes}
                  />
                ) : (
                  <ChevronRightIcon />
                )}
              </IconButton>
            </DrawerHeader>
            <Divider />
            <List>
              {menuItems.length === 0 ? (
                <ListItem>
                  <ListItemText primary="Nenhum menu disponível" />
                </ListItem>
              ) : (
                menuItems.map((menuItem) => {
                  // Validar estrutura do menuItem
                  if (!menuItem || !menuItem.menu) {
                    return null;
                  }

                  const hasSubmenus = menuItem.submenu && Array.isArray(menuItem.submenu) && menuItem.submenu.length > 0;

                  return (
                    <div key={menuItem.idMenu}>
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => hasSubmenus && handleMenuClick(menuItem.idMenu)}
                          disabled={!hasSubmenus}
                        >
                          <ListItemText
                            primary={menuItem.menu}
                            primaryTypographyProps={{
                              fontWeight: "bold",
                            }}
                          />
                          {hasSubmenus && (
                            openMenus.includes(menuItem.idMenu) ? (
                              <ExpandLess />
                            ) : (
                              <ExpandMore />
                            )
                          )}
                        </ListItemButton>
                      </ListItem>
                      {hasSubmenus && (
                        <Collapse
                          in={openMenus.includes(menuItem.idMenu)}
                          timeout="auto"
                          unmountOnExit
                        >
                          {renderSubmenus(
                            menuItem.submenu,
                            menuItem.idMenu,
                            menuItem.menu
                          )}
                        </Collapse>
                      )}

                      
                    </div>
                  );
                })
              )}
            </List>
          </Drawer>
        </Box>
        <div style={{ marginTop: "5rem" }} />
      </div>
      {/* Componente necessário para englobar todos os childrens das routas */}
      <Outlet />
      <AlertNotification
        handleOpen={openAlertNotification}
        notificatDescript={textNotifications}
        handleCloseNotifications={handleCloseNotifications}
      />
    </>
  );
}
